import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Header from "@/components/Header";
import Onboarding from "@/components/Onboarding";
import CheckoutBanner from "@/components/CheckoutBanner";
import BalanceBanner from "@/components/BalanceBanner";
import Browse from "@/pages/Browse";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";
import FreePage from "@/pages/FreePage";
import VerifyOtp from "@/pages/VerifyOtp";
import VerifyRequiredDialog from "@/components/VerifyRequiredDialog";
import ListingDetail from "@/pages/ListingDetail";
import Checkout from "@/pages/Checkout";
import SellChat from "@/pages/SellChat";
import EditListing from "@/pages/EditListing";
import Orders from "@/pages/Orders";
import Profile from "@/pages/Profile";
import PaymentsVerification from "@/pages/PaymentsVerification";
import Watchlist from "@/pages/Watchlist";
import Notifications from "@/pages/Notifications";
import Deposit from "@/pages/Deposit";
import PaymentSuccess from "@/pages/PaymentSuccess";
import CompleteProfile from "@/pages/CompleteProfile";
import TopUp from "@/pages/TopUp";
import AdminGate from "@/pages/AdminGate";
import LegalPage from "@/pages/LegalPage";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";

function Protected({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-slate-400">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && !user.is_admin) return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  const { user, refreshUser } = useAuth();
  const location = useLocation();

  // BUG 17: admins are confined to the admin dashboard — except listing view/edit (ADMIN FIX 9) and public legal pages (preview).
  if (user?.is_admin && location.pathname !== "/admin" && !/^\/listing\//.test(location.pathname) && !/^\/(terms|refunds|privacy)$/.test(location.pathname)) {
    return <Navigate to="/admin" replace />;
  }

  // BUG 16: first-login onboarding carousel for non-admin users.
  const onboardingBlockedRoutes = location.pathname === "/deposit" || location.pathname === "/sell" || location.pathname === "/verify" || location.pathname === "/complete-profile";
  const showOnboarding = !!user && !user.is_admin && user.has_completed_onboarding === false && !onboardingBlockedRoutes;
  const completeOnboarding = async () => {
    try { await api.put("/auth/profile", { has_completed_onboarding: true }); } catch { /* ignore */ }
    refreshUser();
  };

  if (showOnboarding) {
    return <Onboarding onDone={completeOnboarding} />;
  }

  return (
    <>
      <Header />
      <BalanceBanner />
      <CheckoutBanner />
      <Routes>
        <Route path="/" element={<Browse />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify" element={<VerifyOtp />} />
        <Route path="/listing/:id" element={<ListingDetail />} />
        <Route path="/free" element={<FreePage />} />
        <Route path="/listing/:id/edit" element={<Protected><EditListing /></Protected>} />
        <Route path="/sell" element={<SellChat />} />
        <Route path="/orders" element={<Protected><Orders /></Protected>} />
        <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
        <Route path="/deposit" element={<Protected><Deposit /></Protected>} />
        <Route path="/deposit/success" element={<Protected><PaymentSuccess kind="deposit" /></Protected>} />
        <Route path="/deposit/topup/success" element={<Protected><PaymentSuccess kind="topup" /></Protected>} />
        <Route path="/checkout/success" element={<Protected><PaymentSuccess kind="purchase" /></Protected>} />
        <Route path="/checkout/:listingId" element={<Protected><Checkout /></Protected>} />
        <Route path="/complete-profile" element={<Protected><CompleteProfile /></Protected>} />
        <Route path="/deposit/topup" element={<Protected><TopUp /></Protected>} />
        <Route path="/profile" element={<Protected><Profile /></Protected>} />
        <Route path="/payments" element={<Protected><PaymentsVerification /></Protected>} />
        <Route path="/watchlist" element={<Protected><Watchlist /></Protected>} />
        <Route path="/terms" element={<LegalPage pageKey="terms" />} />
        <Route path="/refunds" element={<LegalPage pageKey="refunds" />} />
        <Route path="/privacy" element={<LegalPage pageKey="privacy" />} />
        <Route path="/how-it-works" element={<LegalPage pageKey="how-it-works" />} />
        <Route path="/returns" element={<LegalPage pageKey="returns" />} />
        <Route path="/delivery" element={<LegalPage pageKey="delivery" />} />
        <Route path="/pricing" element={<LegalPage pageKey="pricing" />} />
        <Route path="/seller-guide" element={<LegalPage pageKey="seller-guide" />} />
        <Route path="/about" element={<LegalPage pageKey="about" />} />
        <Route path="/contact" element={<LegalPage pageKey="contact" />} />
        <Route path="/help" element={<LegalPage pageKey="help" />} />
        <Route path="/prohibited-items" element={<LegalPage pageKey="prohibited-items" />} />
        <Route path="/counterfeit-policy" element={<LegalPage pageKey="counterfeit-policy" />} />
        <Route path="/admin" element={<AdminGate />} />
      </Routes>
      {!location.pathname.startsWith("/admin") && location.pathname !== "/sell" && <Footer />}
      {!location.pathname.startsWith("/admin") && location.pathname !== "/sell" && <WhatsAppButton />}
      <VerifyRequiredDialog />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Shell />
          <Toaster
            position="top-center"
            offset={72}
            closeButton
            icons={{ error: <span data-icon className="text-red-500"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg></span> }}
            toastOptions={{ classNames: {
              toast: "bg-white text-slate-900 border border-slate-200 shadow-lg rounded-xl",
              error: "bg-white text-slate-900 border-slate-200 [&_[data-icon]]:text-red-500",
              closeButton: "bg-white border border-slate-200 text-slate-500",
            } }}
          />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
